import { LoadingButton } from "@mui/lab";
import { Box, Typography, Card, CardContent, Button, Divider, Link, useTheme, useMediaQuery, Dialog } from "@mui/material";
import ScheduleWithAgentModal from "components/Modals/ScheduleWithAgentModal";
import { useRemoteConfig } from "contexts/RemoteConfig";
import { useSettings } from "contexts/Settings";
import { useAuth } from "contexts/User";
import { mixpanelService as mixpanel } from 'services/tracking';
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Interfaces, Mutations, useMutation } from 'services/apollo';
import { shouldDisableApplicationButton } from "utils/calcEnrollmentAppCutoff";
import { HEALTH_PLAN_EVENTS, getPageViewSource } from 'utils/trackingUtils';

function SelectPlanModal(props: { plan: Interfaces.QuotitHealthPlanDetails, close: Function, userId?: string }) {
  const { settings } = useSettings();
  const navigate = useNavigate()
  const location = useLocation();
  const [scheduleWithAgent, setScheduleWithAgent] = useState<boolean>(false);
  const plan = props.plan;
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  const { answers, user, userClaims } = useAuth();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const FF_disableNewApplicationCutoffDate = useRemoteConfig("disableNewApplicationCutoffDate").asBoolean() || false;
  const [createApplication, { loading: loadingApplication }] = useMutation(Mutations.createApplication);

  const handleStartApplication = async () => {
    if (!user) {
      throw new Error('User is not logged in.');
    }
    
    // Track application started
    mixpanel.track(HEALTH_PLAN_EVENTS.HEALTH_PLAN_APPLICATION_STARTED, {
      page_view: getPageViewSource(location.pathname),
      plan_id: plan.id,
      plan_name: plan.name,
      carrier: plan.carrier.name,
      benefit_level: plan.benefitLevel,
      insurance_type: plan.insuranceType,
      monthly_premium: plan.rate
    });
    
    try {
      const { data } = await createApplication({
        variables: {
          userId: props?.userId || user?.uid,
          planId: plan.id,
        },
      });

      // Check if application creation was successful
      if (data?.createApplication?.success) {
        if (userClaims?.admin) {
          navigate(`/admin/applications/${data?.createApplication?.applicationId}`);
        } else {
          navigate(`/account/applications/${data?.createApplication?.applicationId}`);
        }
      }
      else {
        console.error('Application creation failed or success status not received.');
      }

    } catch (error) {
      console.error('Error creating application', error);
    }
  };

  useEffect(() => {
    const applicationEffectiveDate = answers?.get("insuranceDetails.effectiveDate");

    const shouldDisableNewApplicationButton = shouldDisableApplicationButton(
      applicationEffectiveDate,
      FF_disableNewApplicationCutoffDate,
      false // Use new business day rules instead of legacy 72-hour rule
    );

    setIsButtonDisabled(shouldDisableNewApplicationButton);
  }, [answers, FF_disableNewApplicationCutoffDate]);

  useEffect(() => {
    mixpanel.track("SelectHealthPlan", {
      plan: {
        name: props.plan.name,
        carrier: props.plan.carrier.name,
        id: props.plan.id,
        type: props.plan.planType
      }
    });
  });

  return (
    <Card
      sx={{
        backgroundColor: "white.main",
        height: { xs: "100vh", sm: "auto" },
        minWidth: "50vw",
        overflowY: "scroll",
        p: 4,
      }}
    >
      <CardContent
        sx={{
          textAlign: "left",
          display: "flex",
          flexDirection: "column",
          gap: "33px",
          padding: 0,
        }}
      >
        <Box>
          <Typography id="select-plan-modal-title" variant="h2" fontWeight={600} align="center" color={"primary.main"}>
            How to Enroll
          </Typography>
          <Typography align="center">
            {plan.carrier.name} - {plan.name}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 4,
            justifyContent: "center",
          }}
        >
          <Box
            width={"100%"}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography variant="h2" align="center" data-testid="submit-online-application">
              Submit an online application
            </Typography>
            <Typography>
              When can submit your application online on your behalf. Complete our enrollment form and our team
              will process your enrollment. Click &quot;Start application&quot; to get started!
            </Typography>
            <Typography variant="caption">
              Note: You may submit an online application up to 1 business day before the start date of the
              application. If you would like to get enrolled in a plan after the online cutoff, please call our
              Member Services team at +1 831-288-6412. Our team is available Monday through Friday, 9am to 7pm
              Central Time
            </Typography>
            <LoadingButton
              loading={loadingApplication}
              data-test-id="start-application-button"
              disabled={isButtonDisabled}
              onClick={handleStartApplication}
              variant="contained"
              color="primary"
            >
              Start application
            </LoadingButton>
          </Box>
          <Divider
            orientation={isSm ? "vertical" : "horizontal"}
            flexItem
            variant="middle"
            sx={{ backgroundColor: "brightOrange.main" }}
          />
          <Box
            width={"100%"}
            sx={{
              maxWidth: "600px",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography variant="h2" align="center">
              Get Help or Enroll live with an agent
            </Typography>
            <Typography>
              When&apos;s team of licensed insurance agents are here to help you select a plan and get you enrolled.
              To speak with one of our agents, call us at &nbsp;
              <Link
                href={"tel:" + settings.REACT_APP_CONCIERGE_PHONE}
                color="primary"
                style={{ whiteSpace: "nowrap" }}
              >
                {settings.REACT_APP_CONCIERGE_PHONE}
              </Link>
              &nbsp;Our team is available Monday through Friday, 9am to 7pm Central Time.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ mx: "auto" }}>
          <Button
            variant="outlined"
            onClick={() => {
              props.close();
            }}
          >
            Close
          </Button>
        </Box>

        <Dialog
          open={scheduleWithAgent}
          onClose={() => {
            setScheduleWithAgent(false);
          }}
          fullScreen={isSm ? false : true}
          maxWidth="lg"
        >
          <ScheduleWithAgentModal
            title="Schedule a Call with an Agent About This Policy"
            close={() => {
              setScheduleWithAgent(false);
            }}
          />
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default SelectPlanModal;
